import React, { useState } from "react";

import { Box, Button, CircularProgress, Divider, Grid, IconButton, Menu, MenuItem, Slider, Typography } from "@mui/material";
import { Add, Edit, MoreVert } from "@mui/icons-material";

import { DataLayout, FieldControl, StatusChip } from "../../../shared/components";

import { useDisplayField } from "../../../hooks/useDisplayField";
import { formatDate } from "../../../shared/utils/formatDate";
import { useCapabilities } from "../../../hooks/useCapabilities";

interface RaiseMitigationProps {
    isMitigationFetching: boolean;
    isMitigationPending: boolean;
    setMitigationsData: (data: any) => void;
    onClearMitigation: () => void;
    isSaving: boolean;
    onSaveMitigation: () => void;
    editMitigationStatus: any;
    mitigationData: any;
    mitigationsData: any;
    onEditMitigation: (cause: any) => void;
}

export const RaiseMitigation: React.FC<RaiseMitigationProps> = ({ mitigationsData, onEditMitigation, isMitigationFetching, isMitigationPending, setMitigationsData, onClearMitigation, isSaving, onSaveMitigation, editMitigationStatus, mitigationData }) => {

    const { isAllowedAddMitigation, isAllowedEditMitigation } = useCapabilities();

    const [formStatus, setFormStatus] = useState<boolean>(false);

    const { getFilterOptions } = useDisplayField([]);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selected, setSelected] = useState<any>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, cause: any) => {
        event.stopPropagation();
        setSelected(cause);
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    if (isMitigationFetching || isMitigationPending) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box display={'flex'} flexDirection={'column'} height={'100%'}>
            {
                !formStatus && isAllowedAddMitigation && (
                    <Button onClick={() => setFormStatus(true)} variant="outlined">Add Mitigation</Button>
                )
            }
            {
                formStatus && (
                    <DataLayout title={"Add Mitigations"} bgVariant="filled">
                        <Grid container spacing={2} p={2}>
                            <Grid size={6}>
                                <FieldControl
                                    keyName={"impactCategory"}
                                    label={"Impact"}
                                    variant={"select"}
                                    options={getFilterOptions('RISK_IMPACT').map((value) => {
                                        return {
                                            label: value.display_text,
                                            value: value.key
                                        }
                                    })}
                                    value={mitigationsData.impactCategory}
                                    onChange={(key: string, value: any) => {
                                        setMitigationsData({ ...mitigationsData, [key]: value });
                                    }}
                                />
                            </Grid>
                            <Grid size={6}>
                                <FieldControl
                                    keyName={"impactTitle"}
                                    label={"Title"}
                                    variant={"input"}
                                    value={mitigationsData.impactTitle}
                                    onChange={(key: string, value: any) => {
                                        setMitigationsData({ ...mitigationsData, [key]: value });
                                    }}
                                />
                            </Grid>
                            <Grid size={12}>
                                <FieldControl
                                    keyName={"impactDescription"}
                                    label={"Description"}
                                    variant={"input"}
                                    value={mitigationsData.impactDescription}
                                    onChange={(key: string, value: any) => {
                                        setMitigationsData({ ...mitigationsData, [key]: value });
                                    }}
                                />
                            </Grid>
                            <Grid size={12}>
                                <FieldControl
                                    keyName={"impactOwner"}
                                    label={"Owner"}
                                    variant={"select"}
                                    options={getFilterOptions('RISK_OWNER').map((value) => {
                                        return {
                                            label: value.display_text,
                                            value: value.key
                                        }
                                    })}
                                    value={mitigationsData.impactOwner}
                                    onChange={(key: string, value: any) => {
                                        setMitigationsData({ ...mitigationsData, [key]: value });
                                    }}
                                />
                            </Grid>
                            <Grid size={12} container border={1} borderColor={'divider'} borderRadius={1} padding={2} spacing={2}>
                                <Grid size={12} display={'flex'} alignItems={'center'} gap={1}>
                                    <Typography variant="subtitle2" color="text.primary">Current Impact</Typography>
                                    <Box ml={'auto'} display={'flex'} alignItems={'center'} gap={1}>
                                        <Typography variant="caption" color="text.secondary">Score</Typography>
                                        <StatusChip label={String(Number(mitigationsData.impact) * Number(mitigationsData.probability))} color="amber" variant="filled" sx={{ borderRadius: '4px', px: .5 }} />
                                    </Box>
                                </Grid>
                                <Grid size={12}>
                                    <Divider />
                                </Grid>
                                <Grid size={12}>
                                    <Box sx={{ px: 2 }}>
                                        <Typography variant="subtitle2" color="text.primary">Impact</Typography>
                                        <Slider
                                            value={mitigationsData.impact}
                                            onChange={(_, value) =>
                                                setMitigationsData({ ...mitigationsData, impact: value as number })
                                            }
                                            valueLabelDisplay="auto"
                                            step={1}
                                            marks
                                            min={1}
                                            max={5}
                                            sx={{ mt: 2 }}
                                        />
                                        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                                            {
                                                getFilterOptions('RISK_SCORE').map((value) => (
                                                    <Typography variant="caption">{value.display_text}</Typography>
                                                ))
                                            }
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid size={12}>
                                    <Box sx={{ px: 2 }}>
                                        <Typography variant="subtitle2" color="text.primary">Probability</Typography>
                                        <Slider
                                            value={mitigationsData.probability}
                                            onChange={(_, value) =>
                                                setMitigationsData({ ...mitigationsData, probability: value as number })
                                            }
                                            valueLabelDisplay="auto"
                                            step={1}
                                            marks
                                            min={1}
                                            max={5}
                                            sx={{ mt: 2 }}
                                        />
                                        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                                            {
                                                getFilterOptions('RISK_SCORE').map((value) => (
                                                    <Typography variant="caption">{value.display_text}</Typography>
                                                ))
                                            }
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                            <Grid size={12}>
                                <Divider />
                            </Grid>
                            <Grid size={12} display={'flex'} justifyContent={'flex-end'} gap={2}>
                                <Button
                                    sx={{ color: 'text.primary' }}
                                    disabled={isSaving}
                                    onClick={() => {
                                        onClearMitigation();
                                        setFormStatus(false);
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    startIcon={<Add />}
                                    loading={isSaving}
                                    disabled={isSaving}
                                    onClick={onSaveMitigation}
                                >
                                    {editMitigationStatus.editMitigationStatus ? 'Edit mitigation' : 'add mitigations'}
                                </Button>
                            </Grid>
                        </Grid>
                    </DataLayout>
                )
            }

            <Box flex={1} display={'flex'} flexDirection={'column'} gap={2} sx={{ overflowY: 'auto' }} mt={2}>
                {
                    mitigationData?.length == 0 ?
                        <Typography variant="body2" color="text.secondary" textAlign="center" mt={5}>No Mitigations found.</Typography> :
                        mitigationData.map((mitigation: any) => (
                            <Box display={'flex'} gap={1} key={mitigation.id} border={1} borderColor={'divider'} flexWrap={'wrap'} justifyContent={'space-between'} alignItems={'center'} borderRadius={1} padding={2}>
                                <Box display={'flex'} flexDirection={'column'} alignItems={'start'} gap={.5}>
                                    <Typography variant="subtitle2" color="text.primary">{mitigation.title || 'N/A'}</Typography>
                                    <Box display={'flex'} alignItems={'center'} gap={.5}>
                                        <Typography variant="caption" color="text.secondary">
                                            {mitigation.owner_display_text}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            •
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {mitigation.description || 'N/A'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            •
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Last Review: {mitigation.updated_at ? formatDate(mitigation.updated_at) : formatDate(mitigation.created_at)}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box display={'flex'} alignItems={'center'} gap={.5}>
                                    {
                                        isAllowedEditMitigation && (
                                            <IconButton onClick={(e) => handleMenuOpen(e, mitigation)}>
                                                <MoreVert fontSize="small" />
                                            </IconButton>
                                        )
                                    }
                                </Box>
                                <Box display={'flex'} alignItems={'center'} gap={.5} width={'100%'}>
                                    <Typography variant="caption" color="text.secondary">Impact</Typography>
                                    <StatusChip label={mitigation.residual_impact || 'N/A'} variant="filled" color="amber" sx={{ borderRadius: '4px', mr: 2, ml: 1 }} />
                                    <Typography variant="caption" color="text.secondary">Probability</Typography>
                                    <StatusChip label={mitigation.residual_probability || 'N/A'} variant="filled" color="amber" sx={{ borderRadius: '4px', mr: 2, ml: 1 }} />
                                </Box>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={handleMenuClose}
                                >
                                    <MenuItem
                                        onClick={() => {
                                            onEditMitigation(selected);
                                            setFormStatus(true);
                                            handleMenuClose();
                                        }}
                                    >
                                        <Edit fontSize="small" />
                                        <Typography ml={1}>Edit</Typography>
                                    </MenuItem>
                                </Menu>
                            </Box>
                        ))
                }
            </Box>
        </Box>
    )
}